'use client';

import { Suspense, useEffect, useState } from 'react';
import { useMutationState } from '@tanstack/react-query';
import { Transition } from '@headlessui/react';
import { Dictionary } from '@ts/dictionary';
import { Variant } from '@ts/variant';
import { cn } from '@ngg/storefront-utils';
import {
  Carousel,
  CarouselContent,
  CarouselNext,
  CarouselPrevious,
} from '@ui/components/Carousel/Carousel';
import { MutationKeys } from '@utils/mutations/mutationKeys';
import useMutateCart from '@utils/hooks/cart/useMutateCart';
import ProductCard from '@ui/components/ProductCard/ProductCard';
import Thumbnail from '@ui/components/Thumbnail/Thumbnail';
import Button from '@ui/components/button/buttonUI';
import Link from '@ui/components/Link/Link';
import Loading from '@ui/components/Loading/Loading';
import Icon from '@ui/components/icon/icon';
import Paragraph from '../Paragraph/Paragraph';

const ProductItem = ({
  product,
  locale,
  dictionary,
}: {
  product: Variant;
  locale: string;
  dictionary: Dictionary;
}) => {
  const prodId = product?.id;
  const addToCart = useMutateCart({
    locale,
    type: 'add',
  });
  const handleAddToCart = (partNo: string) => {
    addToCart.mutate({
      items: [
        {
          partNo,
          quantity: 1,
        },
      ],
    });
  };

  const addedToCart = addToCart.isSuccess;

  return (
    <div
      key={product?.id}
      className={cn([
        'flex flex-col justify-center',
        'min-w-[175px] max-w-[175px]',
        'md:min-w-[222px] md:max-w-[222px]',
      ])}>
      <Link href={product?.MainProductURL}>
        <ProductCard
          productId={product.id}
          productType="Variant"
          title={product.title}
          brand={product['norce_brand']}
          priceDictionary={{
            from: dictionary?.from,
            recommendedPrice: dictionary?.recommendedPrice,
          }}
          priceDetails={{
            priceType: product?.priceType,
            fromPrice: product?.fromPrice,
            price: product?.price,
            presentationPrice: product?.presentationPrice,
            presentationOriginal: product?.presentationOriginal,
            presentationRrpWhenCampaign: product?.rrpWhenCampaign,
            rrpWhenCampaign: product?.rrpWhenCampaign,
            originalPrice: product?.originalPrice,
          }}
          key={product.id}
          classNames={{
            price: 'pb-4',
            title: 'pb-4',
          }}
          stockStatus={{ stockStatus: product.stockStatus }}
          stockStatusDictionary={dictionary}>
          <Suspense>
            <Thumbnail title={product.title} src={product.ImageURL} />
          </Suspense>
        </ProductCard>
      </Link>

      <Button
        type="button"
        disabled={addedToCart || addToCart.isPending}
        isLoading={addToCart.isPending}
        onClick={() => handleAddToCart(prodId)}
        className={cn([
          'flex items-center justify-center',
          'btn-primary max-h-8 grow border border-borderColor bg-main px-3 py-2 text-sm font-bold tracking-wide text-black transition-colors',
          (addToCart.isPending || addedToCart) &&
            'border-black bg-black text-white aria-disabled:opacity-100',
          'hover:border-black hover:bg-black hover:text-white',
        ])}
        LoadingComponent={Loading}
        fullWidth>
        {!addToCart.isPending && addedToCart && dictionary?.added}
        {!addToCart.isPending && !addedToCart && dictionary?.addToCart}
      </Button>
    </div>
  );
};

interface ProductAddCarouselProps {
  dictionary: Dictionary;
  products: Variant[];
  locale: string;
}

const ProductAddCarousel = ({
  products,
  dictionary,
  locale,
}: ProductAddCarouselProps) => {
  const [cartHasBeenPending, setCartHasBeenPending] = useState(false);
  const [addToCartSucceeded, setAddToCartHasSuceeded] = useState(false);
  const [carouselClosed, setCarouselClosed] = useState(false);

  const cartMutationStatus = useMutationState({
    filters: { mutationKey: [MutationKeys.cart, locale] },
    select: (mutation) => mutation.state.status,
  });

  useEffect(() => {
    if (addToCartSucceeded) {
      return;
    }
    if (!cartMutationStatus.length) {
      return setCartHasBeenPending(false);
    }
    if (cartHasBeenPending && cartMutationStatus.pop() === 'success') {
      return setAddToCartHasSuceeded(true);
    }
    if (cartMutationStatus.some((s) => s === 'pending')) {
      return setCartHasBeenPending(true);
    }
  }, [cartMutationStatus, cartHasBeenPending, addToCartSucceeded]);

  return (
    <Transition
      as="section"
      enter="transition ease duration-500 transform"
      enterFrom="opacity-0 -translate-y-12"
      enterTo="opacity-100 translate-y-0"
      leave="transition ease duration-300 transform"
      leaveFrom="opacity-100 translate-y-0"
      leaveTo="opacity-0 -translate-y-12"
      show={addToCartSucceeded && !carouselClosed}>
      <button
        type="button"
        className="absolute -top-[8px] right-0 md:top-0"
        onClick={() => {
          setCarouselClosed(true);
        }}>
        <Icon name="cross" size={16} onClick={() => setCarouselClosed(true)} />
      </button>
      <section className="mx-[-16px] mb-4 border-b pb-6 pt-2 md:pb-10 md:pt-4">
        <div className="relative mx-auto flex max-w-[1064px] flex-col justify-center">
          <Paragraph className="mb-5 text-center text-xl font-bold md:mb-6">
            {dictionary?.recommendedAccessories}
          </Paragraph>
          <Carousel
            opts={{
              align: 'start',
              slidesToScroll: 'auto',
            }}
            className="flex justify-center px-4 md:px-0">
            <CarouselContent
              className="gap-4"
              contentContainerClassName="max-w-[946px]">
              {products &&
                products.map((prod) => (
                  <ProductItem
                    key={prod.id}
                    product={prod}
                    locale={locale}
                    dictionary={dictionary}
                  />
                ))}
            </CarouselContent>
            <CarouselPrevious
              variant="secondary"
              className="-translate-y-16 items-start"
            />
            <CarouselNext
              variant="secondary"
              className="-translate-y-16 items-start"
            />
          </Carousel>
        </div>
      </section>
    </Transition>
  );
};

ProductAddCarousel.displayName = 'ProductAddCarousel';

export default ProductAddCarousel;
